<template>
  <a-modal
    :open="open"
    :title="modalTitle"
    width="710px"
    centered
    wrapClassName="modal-divider signature-create-modal"
    @cancel="handleCancel"
    :mask-closable="false"
  >
    <div class="signature-create">
      <p class="signature-create__description">
        Asigna un nombre para identificar la firma y redacta su contenido.
      </p>

      <a-form ref="formRef" layout="vertical" class="signature-create__form" :model="formState" :rules="rules" required>
        <a-form-item 
          name="name"
          label="Nombre de la firma"
          extra="El nombre debe ser único para cada firma"
          required
        >
          <a-input
            v-model:value="formState.name"
            placeholder="Ej: Firma corporativa Entel"
            @pressEnter="handleSave"
            show-count 
            :maxlength="100"
          />
        </a-form-item>

        <a-form-item 
          class="textarea-item"
          label="Contenido de la firma"
          required
        >
          <EditorWithImage
            v-model="formState.content_html"
            class="signature-create__editor"
            placeholder="Diseña tu firma aquí..."
          />
        </a-form-item>
      </a-form>

      <a-divider style="margin: 16px 0"/>

      <div class="signature-create__assigned">
        <h4>Asignación de firma</h4>
        <p>
          Selecciona en qué tipos de correo se añadirá esta firma automáticamente. 
          Si ya tienes una firma asignada para estos, <strong>la actual será reemplazada</strong>.
        </p>
        <div class="select-row">
          <div class="select-group">
            <div class="select-head">
              <a-checkbox v-model:checked="formState.is_new_message">Mensajes nuevos</a-checkbox>
              <a-tooltip title="La firma aparecerá cuando inicies un correo desde cero.">
                <InfoCircleOutlined />
              </a-tooltip>
            </div>
            <div>
              <a-tooltip
                :title="newMessageSignatureName"
                :arrow="false"
                overlayClassName="custom-tooltip"
                placement="bottomLeft"
              >
                <p class="signature-current">Actual: {{newMessageSignatureName || "Sin firma"}}</p>
              </a-tooltip>
            </div>
          </div>

          <div class="select-group">
            <div class="select-head">
              <a-checkbox v-model:checked="formState.is_reply_forward">Respuestas y reenvíos</a-checkbox>
              <a-tooltip title="La firma aparecerá cuando respondas o reenvíes un correo.">
                <InfoCircleOutlined />
              </a-tooltip>
            </div>
            <div>
              <a-tooltip
                :title="replySignatureName"
                :arrow="false"
                overlayClassName="custom-tooltip"
                placement="bottomLeft"
              >
                <p class="signature-current">Actual: {{replySignatureName || "Sin firma"}}</p>
              </a-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <a-button key="back" @click="handleCancel">Cancelar</a-button>
      <span style="margin-inline-start: 8px;">
        <a-tooltip>
          <template #title v-if="isSaveDisabled">
            Completa todos los campos obligatorios para crear la firma.
          </template>
          <a-button
            key="submit"
            type="primary"
            :loading="loading"
            @click="handleSave"
            :disabled="isSubmitDisabled"
          >
            {{buttonText}}
          </a-button>
        </a-tooltip>
      </span>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch, createVNode } from "vue";
import { InfoCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons-vue";
import { cloneDeep, debounce, isEqual } from "lodash";

import EditorWithImage from "@/app/emails/components/editor/EditorWithImage.vue";
import { normalizeEditorImageHtml } from "@/app/emails/utils/normalizeEditorImageHtml";
import { emailService } from "../../services/Email.service.ts";
import { Signature, CreateSignature } from "@/@types/emails";
import { message, Modal } from "ant-design-vue";
import { ResponseError } from "@/app/shared/utils/httpManager";

const props = defineProps<{
  open: boolean;
  mode: string;
  signature?: Signature | null;
  newMessageSignatureName?: string | null;
  replySignatureName?: string | null;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
  saved: [];
}>();

const loading = ref(false);
const formRef = ref();

const modalTitle = computed(() =>
  props.mode === "edit" ? "Editar firma" : "Crear firma"
);

const buttonText = computed(() =>
  props.mode === "edit" ? "Guardar cambios" : "Crear firma"
);

const isEditorContentEmpty = (html: string) => {
  if (!html) return true;

  const parsedDocument = new DOMParser().parseFromString(html, "text/html");
  const plainText = parsedDocument.body.textContent?.replace(/\u00a0/g, " ").trim() ?? "";

  if (plainText) return false;

  return !parsedDocument.body.querySelector("img,table,svg,video,iframe");
};

const formState = ref<CreateSignature>({
  name: "",
  content_html: "",
  is_new_message: false,
  is_reply_forward: false,
});

const formStateCompare = ref(cloneDeep(formState.value));

const hasChanges = computed(() => {
  return !isEqual(formState.value, formStateCompare.value);
});

const isSameName = computed(() => {
  return props.mode === "edit" && formState.value.name === formStateCompare.value.name;
});

const checkNameExists = async (name: string) => {
  if (!name || !name.trim()) return false;

  try {
    const result = await emailService.validateName(name.trim());
    return result.exists;
  } catch (error) {
    console.error("Error verificando nombre:", error);
    return false;
  }
};

const debouncedValidator = debounce(async (name, callback) => {
  try {
    const exists = await checkNameExists(name);
    if (exists) {
      callback("Este nombre de firma ya existe. Prueba con uno diferente.");
    } else {
      callback();
    }
  } catch (error) {
    console.error(error);
    callback();
  }
}, 400);

const rules = {
  name: [
    { required: true, message: "Campo requerido" },
    {
      validator: (_: any, value: string) =>
        new Promise((resolve, reject) => {
          if (isSameName.value) return resolve(true);

          debouncedValidator(value, (msg: string) => {
            msg ? reject(msg) : resolve(true);
          });
        }),
      trigger: ["change", "blur"],
    },
  ],
};

const isSaveDisabled = computed(() => {
  return !formState.value.name.trim() || isEditorContentEmpty(formState.value.content_html);
});

const isSubmitDisabled = computed(() => {
  return isSaveDisabled.value || (props.mode === "edit" && !hasChanges.value);
});

const resetForm = () => {
  formState.value.name = "";
  formState.value.content_html = "";
  formState.value.is_new_message = false;
  formState.value.is_reply_forward = false;
};

const closeModal = () => {
  emit("update:open", false);
  resetForm();
  formRef.value?.clearValidate?.();
};

const handleSave = async () => {
  try {
    await formRef.value?.validate();
  } catch {
    return;
  }

  if (isSubmitDisabled.value) return;

  loading.value = true;

  try {
    const payload = cloneDeep(formState.value);
    payload.content_html = normalizeEditorImageHtml(payload.content_html);
    const replaceSignatureFlag = async (
      field: "is_new_message" | "is_reply_forward",
      signatureId: number
    ) => {
      const currentSignatures = await emailService.getSignatureList();
      const requests = currentSignatures
        .filter((signature) => signature[field] && signature.id !== signatureId)
        .map((signature) =>
          emailService.updateSignature(signature.id, {
            [field]: false,
          } as Partial<CreateSignature>)
        );

      await Promise.all(requests);
    };

    if (props.mode === "edit" && props.signature) {
      await emailService.updateSignature(props.signature.id, payload);

      if (payload.is_new_message) {
        await replaceSignatureFlag("is_new_message", props.signature.id);
      }

      if (payload.is_reply_forward) {
        await replaceSignatureFlag("is_reply_forward", props.signature.id);
      }

      message.success("Firma actualizada con éxito.");
    } else {
      const createdSignature = await emailService.createSignature(payload);

      if (createdSignature.is_new_message) {
        await replaceSignatureFlag("is_new_message", createdSignature.id);
      }

      if (createdSignature.is_reply_forward) {
        await replaceSignatureFlag("is_reply_forward", createdSignature.id);
      }

      message.success("Firma creada con éxito.");
    }

    emit("saved");
    closeModal();
  } catch (error) {
    let errorMessage = "Ha ocurrido un error";
    if (error instanceof ResponseError) errorMessage = error.getErrorMessage();
    message.error(errorMessage);
  } finally {
    loading.value = false;
  }
};

const handleCancel = () => {
  if (hasChanges.value) {
    Modal.confirm({
      title: "¿Deseas descartar los cambios realizados?",
      content: "Recuerda que si no guardas los cambios registrados, estos se perderán.",
      width: 450,
      icon: createVNode(ExclamationCircleOutlined),
      onOk: () => closeModal(),
      centered: true,
      okText: "Sí, descartar",
      cancelText: "No, seguir editando",
    });
  } else {
    closeModal();
  }
};

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) {
      debouncedValidator.cancel();
      formRef.value?.clearValidate?.();
      return;
    }

    if (props.mode === "edit" && props.signature) {
      formState.value.name = props.signature.name;
      formState.value.content_html = props.signature.content_html;
      formState.value.is_new_message = props.signature.is_new_message;
      formState.value.is_reply_forward = props.signature.is_reply_forward;
      formStateCompare.value = cloneDeep(formState.value);
      formRef.value?.clearValidate?.();
      return;
    }

    resetForm();
    formStateCompare.value = cloneDeep(formState.value);
    formRef.value?.clearValidate?.();
  },
  { immediate: true }
);
</script>

<style lang="sass" scoped>
.signature-create
  display: flex
  flex-direction: column
  //gap: 12px

.signature-create__form
  //margin-bottom: 0

.signature-create__form :deep(.ant-form-item-explain),
.signature-create__form :deep(.ant-form-item-extra)
  font-size: 12px
  min-height: 20px

.textarea-item
  margin-bottom: 0

.select-row
  display: grid
  grid-template-columns: repeat(2, 190px)
  gap: 16px
  align-items: start
  justify-content: start

.select-group
  display: flex
  flex-direction: column
  gap: 4px

.select-head
  display: flex
  align-items: center
  gap: 4px

.select-head :deep(.ant-checkbox + span)
  padding-inline-end: 0

.select-group p
  margin: 0
  padding-left: 24px
  font-size: 12px
  line-height: 20px
  color: #00000073

.signature-current
  max-width: 190px
  overflow: hidden
  white-space: nowrap
  text-overflow: ellipsis
  display: inline-block

</style>

<style>
.signature-create-modal .ant-modal-body {
  padding: 20px 24px !important;
}
</style>
